<!--
  (c) Copyright 2024 Calabrio, Inc.
  All Rights Reserved. www.calabrio.com LICENSED MATERIALS
  Property of Calabrio, Inc., Minnesota, USA

  No part of this publication may be reproduced, stored or transmitted,
  in any form or by any means (electronic, mechanical, photocopying,
  recording or otherwise) without prior written permission from Calabrio, Inc.
-->

<template>
  <div class="wy-analytics-contact-reasons-detail-metric-analysis-chart">
    <div class="signal-wrapper">
      <div class="signal-header">
        Signal Distribution
      </div>
      <div v-if="showChart" class="signal-body" v-loading="loading">
        <div class="signal-body-dropdown">
          <el-dropdown key="signalDistribution" ref="signalDropdown" placement="bottom" trigger="click" @visible-change="(visible) => handleSignalDropdownState(visible)" @command="handleSignalDropdownCommand">
            <div class="signal-dropdown" @mousedown="(e) => e.preventDefault()" :class="{ active: signalDropdown.opened }">
              <span class="signal-dropdown-title">
                <span>View by {{ signalDropdownViewBy }}</span>
                <div :class="{ 'arrow-up': signalDropdown.opened, 'arrow-down': !signalDropdown.opened }"></div>
              </span>
            </div>
            <el-dropdown-menu class="signal-content-wrapper" slot="dropdown" v-show="signalDropdown.opened">
              <div v-for="(option, index) in signalDropdown.options" :key="index">
                <el-dropdown-item :command="option.label" :disabled="option.disabled">
                  <div class="signal-content-item" :class="{ active: option.selected, disabled: option.disabled }">View by {{ option.label }}</div>
                </el-dropdown-item>
              </div>
            </el-dropdown-menu>
          </el-dropdown>
        </div>
        <div class="bar-chart-wrapper">
          <div ref="barChart"></div>
        </div>
        <div ref="hoverChart"></div>
        <div class="rangeSlider">
          <el-switch v-model="showRangeSlider" :disabled="false" active-text="scroll chart" @change="handleRangeSliderChange"></el-switch>
        </div>
        <div class="graph-footer">
          <div v-for="(data, index) in cloneSignalDistributionData" :key="index"><span class="footer-legend" :style="{ backgroundColor: getSignalBarColor(data.label) }"></span>{{ data.label }}</div>
        </div>
      </div>
      <div v-else>
        <el-table :data="[]" style="text-align: center; " />
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import moment from 'moment';
import { mapGetters } from 'vuex';

export default Vue.extend({
  name: 'wy-analytics-contact-reasons-detail-metric-analysis-chart',
  props: ['loading', 'parent', 'activeSubPage', 'chartData'],
  data() {
    return {
      showChart: true,
      showRangeSlider: false,
      dateRangeTypes: {
        day: {
          type: 'day',
          label: 'View by day',
          tickformat: '%b %d %Y',
          duraion: 7,
          disabled: false,
        },
        week: {
          type: 'week',
          label: 'View by week',
          tickformat: '%b %d %Y',
          duraion: 30,
          disabled: false,
        },
        month: {
          type: 'month',
          label: 'View by month',
          tickformat: '%b %Y',
          duraion: 99999,
          disabled: false,
        },
      },
      signalDropdown: {
        options: [
          { label: 'year', selected: false, disabled: false },
          { label: 'quarter', selected: false, disabled: false },
          { label: 'month', selected: false, disabled: false },
          { label: 'week', selected: true, disabled: false },
          { label: 'day', selected: false, disabled: false },
        ],
        opened: false,
      },
      signalDropdownViewBy: 'week', // used by parent to send to backend
      tickformat: '%b %Y',
      cloneSignalDistributionData: null,
    };
  },
  watch: {
    chartData() {
      this.handleChartReload();
    },
  },
  computed: {
    ...mapGetters(['dataModel', 'companyConfig']),

    getSignalDistributionData() {
      if (this.chartData && (!this.chartData.bes || !this.chartData.bas)) {
        return [];
      }

      const data = this.activeSubPage === 'experience' ? this.chartData.bes : this.chartData.bas;

      // if all values of a date are 0, remove that signal name
      if (data) {
        return data.filter((d) => {
          const filtered = d.data.filter((dd) => dd.value >= 0);
          return filtered.length > 0;
        });
      } else {
        return [];
      }
    },
  },
  mounted() {
    this.handleCreateViewByOptions();
    this.$root.$on(`fire-app-clickOverlayModal-events`, () => {
      if(this.parent && this.parent.preMsgObj) {
        this.parent.preMsgObj.visible = false;
        this.parent.preMsgObj.onClose()
      }
      // this.handleCreateViewByOptions();
    });
  },
  methods: {
    generateTraceData() {
      this.cloneSignalDistributionData = JSON.parse(JSON.stringify(this.getSignalDistributionData));
      let hasLastData = false;
      let lastData = this.getSignalDistributionData[this.cloneSignalDistributionData.length - 1];
      if (lastData && (lastData.label == 'BES' || lastData.label == 'BAS')) {
        hasLastData = true;
        this.cloneSignalDistributionData.splice(this.cloneSignalDistributionData.length - 1, 1);
      } else {
        lastData = null;
      }
      let traceData = this.cloneSignalDistributionData.map((data) => {
        return {
          x: data.data.map((d) => d.date),
          y: data.data.map((d) => d.value),
          xStartDate: data.data.map((d) => d.startDate),
          xEndDate: data.data.map((d) => d.endDate),
          name: data.label,
          type: 'bar',
          hoverinfo: 'none',
          marker: {
            color: this.getSignalBarColor(data.label),
          },
          titleLabel: hasLastData ? lastData.label : null,
          titleValue: hasLastData ? lastData.data.map((d) => d.value) : null,
        };
      });
      return traceData;
    },

    handleChartReload() {
      let traceData = this.generateTraceData();
      if (!Object.keys(traceData).length) return;
      let xLength = traceData[0].x.length;
      if (xLength < 20) {
        this.showRangeSlider = false;
      } else {
        this.showRangeSlider = true;
      }
      this.generateSignalBarGraph(traceData);
    },

    handleSignalDropdownState(visible) {
      this.signalDropdown.opened = visible;
    },

    toggleSignalDistribution() {
      this.showSignalDistribution = !this.showSignalDistribution;
    },

    handleSignalDropdownCommand(command) {
      this.signalDropdown.options.forEach((option) => {
        option.selected = option.label === command;
      });
      if (this.signalDropdownViewBy != command) {
        this.signalDropdownViewBy = command;
        this.parent.loadMetricAnalysisChart();
      }
    },

    handleCreateViewByOptions() {
      const dateRangeInDays = this.dataModel && this.dataModel.dateRangeInDays;

      const WEEK = 7;
      const MONTH = 30;
      const QUARTER = 90;
      const YEAR = 365;

      if (dateRangeInDays <= WEEK) {
        this.signalDropdown.options.forEach((option) => {
          this.$set(option, 'disabled', option.label === 'year' || option.label === 'quarter' || option.label === 'month' || option.label === 'week');
          // this.$set(option, 'selected', option.label === 'day');
        });
        this.signalDropdownViewBy = 'day';
        this.showRangeSlider = true;
      } else if (dateRangeInDays <= MONTH) {
        this.signalDropdown.options.forEach((option) => {
          this.$set(option, 'disabled', option.label === 'year' || option.label === 'quarter' || option.label === 'month');
          // this.$set(option, 'selected', option.label === 'week');
        });
        this.signalDropdownViewBy = 'week';
      } else if (dateRangeInDays <= QUARTER) {
        this.signalDropdown.options.forEach((option) => {
          this.$set(option, 'disabled', option.label === 'year' || option.label === 'quarter');
          // this.$set(option, 'selected', option.label === 'month');
        });
        this.signalDropdownViewBy = 'month';
      } else if (dateRangeInDays <= YEAR) {
        this.signalDropdown.options.forEach((option) => {
          this.$set(option, 'disabled', option.label === 'year');
          // this.$set(option, 'selected', option.label === 'quarter');
        });
        this.signalDropdownViewBy = 'quarter';
      } else {
        this.signalDropdown.options.forEach((option) => {
          this.$set(option, 'disabled', false);
          // this.$set(option, 'selected', option.label === 'year');
        });
        this.signalDropdownViewBy = 'year';
      }
      this.initDateRangeRules();
    },

    initDateRangeRules() {
      let dateStartMoment = moment(this.dataModel.dateStart, 'YYYY-MM-DD');
      let dateEndMoment = moment(this.dataModel.dateEnd, 'YYYY-MM-DD');
      this.dateRangeDatesDiff = dateEndMoment.diff(dateStartMoment, 'days', true) + 1;
      let disabledYear = false;
      let disabledQuarter = false;
      let disabledMonth = false;
      let disabledWeek = false;
      this.signalDropdown.options.forEach((option) => {
        if (option.label == 'year') {
          disabledYear = option.disabled;
        }
        if (option.label == 'quarter') {
          disabledQuarter = option.disabled;
        }
        if (option.label == 'month') {
          disabledMonth = option.disabled;
        }
        if (option.label == 'week') {
          disabledWeek = option.disabled;
        }
      });
      let chartViewBy = (this.companyConfig.default || {}).selectedViewBy || 'week';
      if (chartViewBy && chartViewBy.dailyWithinDays && this.dateRangeDatesDiff <= chartViewBy.dailyWithinDays) {
        this.signalDropdown.options.forEach((option) => {
          this.$set(option, 'selected', option.label === 'day');
        });
        this.signalDropdownViewBy = 'day';
      } else if (!disabledWeek && chartViewBy && chartViewBy.weeklyWithinDays && this.dateRangeDatesDiff <= chartViewBy.weeklyWithinDays) {
        this.signalDropdown.options.forEach((option) => {
          this.$set(option, 'selected', option.label === 'week');
        });
        this.signalDropdownViewBy = 'week';
      } else if (!disabledMonth && chartViewBy && chartViewBy.monthlyWithinDays && this.dateRangeDatesDiff <= chartViewBy.monthlyWithinDays) {
        this.signalDropdown.options.forEach((option) => {
          this.$set(option, 'selected', option.label === 'month');
        });
        this.signalDropdownViewBy = 'month';
      } else if (!disabledQuarter && chartViewBy && chartViewBy.quarterlyWithinDays && this.dateRangeDatesDiff <= chartViewBy.quarterlyWithinDays) {
        this.signalDropdown.options.forEach((option) => {
          this.$set(option, 'selected', option.label === 'quarter');
        });
        this.signalDropdownViewBy = 'quarter';
      } else if (!disabledYear && chartViewBy && chartViewBy.yearlyWithinDays && this.dateRangeDatesDiff <= chartViewBy.yearlyWithinDays) {
        this.signalDropdown.options.forEach((option) => {
          this.$set(option, 'selected', option.label === 'year');
        });
        this.signalDropdownViewBy = 'year';
      } else {
        const dateRangeInDays = this.dataModel && this.dataModel.dateRangeInDays;

        const WEEK = 7;
        const MONTH = 30;
        const QUARTER = 90;
        const YEAR = 365;

        if (dateRangeInDays <= WEEK) {
          this.signalDropdown.options.forEach((option) => {
            this.$set(option, 'selected', option.label === 'day');
          });
          this.signalDropdownViewBy = 'day';
          this.showRangeSlider = true;
        } else if (dateRangeInDays <= MONTH) {
          this.signalDropdown.options.forEach((option) => {
            this.$set(option, 'selected', option.label === 'week');
          });
          this.signalDropdownViewBy = 'week';
        } else if (dateRangeInDays <= QUARTER) {
          this.signalDropdown.options.forEach((option) => {
            this.$set(option, 'selected', option.label === 'month');
          });
          this.signalDropdownViewBy = 'month';
        } else if (dateRangeInDays <= YEAR) {
          this.signalDropdown.options.forEach((option) => {
            this.$set(option, 'selected', option.label === 'quarter');
          });
          this.signalDropdownViewBy = 'quarter';
        } else {
          this.signalDropdown.options.forEach((option) => {
            this.$set(option, 'selected', option.label === 'year');
          });
          this.signalDropdownViewBy = 'year';
        }
      }
    },

    generateSignalBarGraph(traceData) {
      const layout = {
        barmode: 'stack',
        showlegend: false,
        // margin: { l: 20, r: 50, t: 30, b: 10, pad: 10},
        xaxis: {
          autorange: true,
          fixedrange: true,
          showgrid: false,
          automargin: true,
          showline: true,
          tickformat: this.tickformat,
          linecolor: 'transparent',
          tickangle: 0,
          rangeslider: { autorange: true },
        },
        yaxis: {
          title: {
            text: 'impact',
            standoff: 50,
          },
          side: 'right',
          fixedrange: true,
          rangemode: 'tozero',
          ticklabelposition: 'outside top',
          // position: 0.8
        },
        height: 400,
        // margin: { l: 20 },
        margin: { l: 40, r: 70, t: 30, b: 0, pad: 10 },
        autosize: true,
        hovermode: 'x',
        bargap: 0.5,
        dragmode: false,
      };

      if (this.signalDropdownViewBy == 'year') {
        this.tickformat = '%Y';
      } else if (this.signalDropdownViewBy == 'quarter') {
        this.tickformat = '%b %d %Y';
      } else if (this.signalDropdownViewBy == 'week') {
        this.tickformat = '%b %d %Y';
      } else if (this.signalDropdownViewBy == 'day') {
        this.tickformat = '%b %d %Y';
      } else if (this.signalDropdownViewBy == 'month') {
        this.tickformat = '%b %Y';
      }

      let xLength = traceData[0].x.length;

      if (xLength > 1 && xLength < 5) {
        layout.xaxis.nticks = xLength + 1;
        if (this.signalDropdownViewBy == 'year') {
          layout.xaxis.tickformat = null;
        }
        traceData.forEach(() => {
          // trace.width = 0.3;
          // trace.width = null;
          layout.xaxis.tickformat = this.tickformat;
        });
      } else {
        if (xLength == 1) {
          layout.xaxis.nticks = 2;
          layout.xaxis.tickformat = this.tickformat;
          traceData.forEach((trace) => {
            trace.width = 0.1;
          });
        } else {
          traceData.forEach((trace) => {
            if (!this.showRangeSlider) {
              layout.xaxis.tickmode = 'array';
              layout.xaxis.tickvals = [trace.x[0], trace.x[Math.round((xLength - 1) / 2)], trace.x[xLength - 1]];
              layout.xaxis.nticks = 3;
              layout.xaxis.tickformat = this.tickformat;
              // trace.width = 0.1;
            } else {
              layout.xaxis.nticks = 3;
              layout.xaxis.tickformat = this.tickformat;
              // trace.width = 0.1;
            }
            if (xLength > 12) {
              trace.width = null;
            } else {
              // trace.width = null;
              // trace.width = 0.5
            }
            // trace.thickness = 30
          });
        }
      }
      if (this.signalDropdownViewBy == 'day') {
        this.scrollableChartSetLayoutByDay(traceData[0].x, xLength, layout);
      }
      if (this.signalDropdownViewBy == 'week') {
        this.scrollableChartSetLayoutByWeek(xLength, layout);
      }
      if (this.signalDropdownViewBy == 'month') {
        this.scrollableChartSetLayoutByMonth(xLength, layout);
      }
      if (this.signalDropdownViewBy == 'quarter') {
        this.scrollableChartSetLayoutByQuarter(xLength, layout);
      }
      if (this.signalDropdownViewBy == 'year') {
        this.scrollableChartSetLayoutByYear(xLength, layout);
      }

      if (!this.showRangeSlider || xLength == 1) {
        layout.xaxis.autorange = true;
        layout.xaxis.automargin = true;
        layout.xaxis.rangeslider = null;
      } else {
        layout.xaxis.autorange = false;
        layout.xaxis.automargin = null;
        layout.xaxis.rangeslider = { autorange: true, thickness: 0.1 };
      }

      const barChartRefs = this.$refs.barChart;
      if (!barChartRefs) return;

      let config = { responsive: true, displayModeBar: false, scrollZoom: false, showTips: false };
      // eslint-disable-next-line no-undef
      Plotly.newPlot(barChartRefs, traceData, layout, config);

      barChartRefs.on('plotly_hover', (data) => {
        data.event.preventDefault();
        data.event.stopPropagation();
        if (!this.checkTotalZero(data.points)) {
          try {
            const points = data.points[0];
            const toolTipHtml = this.buildTooltip(points);
            const gd = this.$refs.hoverChart;
            if (gd) {
              gd.style.display = 'block';
              gd.style.position = 'relative';
              gd.innerHTML = toolTipHtml;
            }
          } catch (error) {
            console.error(error);
          }
        }
      });

      barChartRefs.on('plotly_unhover', () => {
        try {
          const gd = this.$refs.hoverChart;
          if (gd) {
            gd.innerHTML = '';
            gd.style.display = 'none';
          }
        } catch (error) {
          console.error(error);
        }
      });

      var timer=undefined;
      barChartRefs.on('plotly_relayout', (eventdata)=>{
        if(Object.prototype.toString.call(eventdata["xaxis.range"]) === '[object Array]' ) {
          if(timer!==null){
            window.clearTimeout(timer);
          }
          timer = window.setTimeout(function(){ 
            let diff = eventdata["xaxis.range"][1] - eventdata["xaxis.range"][0];
            if(diff <= 1.5 ) {
              if(eventdata["xaxis.range"][0] <= -0.5) {
                let update = {'xaxis.range': [0, 1]}
                // eslint-disable-next-line no-undef
                Plotly.relayout(barChartRefs,update);
              }
              if(eventdata["xaxis.range"][1] >= xLength - 0.5 && xLength >= 2) {
                let update = {'xaxis.range': [xLength - 2, xLength - 0.6]}
                // eslint-disable-next-line no-undef
                Plotly.relayout(barChartRefs,update);
              }
            }
            timer=undefined;
          }, 800);
        }
      })
    },

    checkTotalZero(points) {
      let total = 0;
      points.forEach((point) => {
        total = total + point.value;
      });
      return total == 0;
    },

    buildTooltip(points) {
      const pointX = points.bbox.x1;
      const pointY = points.bbox.y1;
      const dateRange = points.x;
      const startDate = points.data.xStartDate[points.pointIndex];
      const endDate = points.data.xEndDate[points.pointIndex];
      let dateTitle = '';
      if (this.signalDropdownViewBy == 'day') {
        dateTitle = moment(points.x).format('MMM DD, YYYY');
      } else {
        dateTitle = moment(startDate).format('MMM DD, YYYY') + ' - ' + moment(endDate).format('MMM DD, YYYY');
      }

      const signalLabels = this.getSignalLabels();
      const availableDates = this.getSignalDateObject();

      const barData = availableDates[dateRange];
      const titleLabel = points.data.titleLabel;
      const suffix = titleLabel == 'BAS' ? '%' : '';
      const display = titleLabel ? 'block' : 'none';

      const titleValue = points.data.titleValue ? points.data.titleValue[points.pointIndex] + suffix : null;

      // console.log(moment().year(2023).week(1).day('monday'), moment().day("Monday").week('2023-01'), dateRange, availableDates, barData)
      const totalImpact = barData && barData.reduce((acc, curr) => acc + curr, 0);

      return `
      <div class="signal-bar-tooltip" style="left: ${pointX - 30}px; top:${pointY - 800}px">
        <span style="color: #828E9A; ">${dateTitle}</span>
        <div style="display:${display};color:#828E9A;position:relative;top:-7px; }">(${titleLabel} ${titleValue})</div>
        ${signalLabels
          .map((label, index) => {
            if (barData[index] === 0) return; // return if value in date is 0

            const color = this.getSignalBarColor(label);
            return `
              <div>
                <div style="display: flex; align-items:center; gap: 4px"><span class="footer-legend" style="background-color: ${color}"></span>${label}</div>
                <b style="margin-left: 18px;">${barData ? this.formatDecimalData(barData[index]) : 0}</b>
              </div>
            `;
          })
          .join('')}
        <div style="margin-top: 4px; border-top: 1px solid #ebeef5;">
          <div style="margin-top: 12px;">Total impact = ${totalImpact ? this.formatDecimalData(totalImpact) : 0}</div>
        </div>
      </div>`;
    },

    getSignalLabels() {
      return this.cloneSignalDistributionData.map((data) => data.label);
    },

    getSignalDateObject() {
      return this.cloneSignalDistributionData.reduce((acc, data) => {
        data.data.forEach((d) => {
          if (!acc[d.date]) {
            acc[d.date] = [];
          }
          acc[d.date].push(d.value);
        });
        return acc;
      }, {});
    },

    getSignalBarColor(label) {
      const colors = this.parent.breakdownColorScheme;
      return colors[label];
    },

    handleRangeSliderChange(value) {
      this.showRangeSlider = value;
      let traceData = this.generateTraceData();
      this.generateSignalBarGraph(traceData);
    },

    scrollableChartSetLayoutByDay(xData, xLength, layout) {
      let defaultRange = [];
      layout.xaxis.type = 'category';
      // let xLength = this.lineChartData.xAxisData.length
      // over 1 year
      if (xLength > 364) {
        let startDate = xLength - 40;
        let endDate = xLength - 1;
        defaultRange = [startDate, endDate];
        // let startDate = xData[xLength - 40];
        // let endDate = xData[xLength - 1];
        // defaultRange = [moment(startDate).format('YYYY-MM-DD'), moment(endDate).format('YYYY-MM-DD')];
      }
      // 3 month to 1 year
      if (xLength > 90 && xLength <= 364) {
        let startDate = xLength - 30;
        let endDate = xLength - -1;
        defaultRange = [startDate, endDate];
        // let startDate = xData[xLength - 30];
        // let endDate = xData[xLength - 1];
        // defaultRange = [moment(startDate).format('YYYY-MM-DD'), moment(endDate).format('YYYY-MM-DD')];
      }
      // 7 days to  3 month

      if (xLength > 7 && xLength <= 90) {
        let startDate = xLength - 6;
        let endDate = xLength - 1;
        defaultRange = [startDate, endDate];
        // let startDate = xData[xLength - 6];
        // let endDate = xData[xLength - 1];
        // // console.log(endDate,  moment(endDate).add(+1, 'days').format('YYYY-MM-DD'))
        // defaultRange = [moment(startDate).format('YYYY-MM-DD'), moment(endDate).format('YYYY-MM-DD')];
        // defaultRange = [moment(startDate).format('YYYY-MM-DD'),moment(endDate).add(+1, 'days').format('YYYY-MM-DD')]
      }
      // layout.xaxis.type = "category"
      // less than 7 days
      if (xLength > 1 && xLength <= 7) {
        // let startDate = xData[0];
        // let endDate = xData[xLength - 1];
        // // console.log( moment(endDate))
        // defaultRange = [moment(startDate).format('YYYY-MM-DD'), moment(endDate).format('YYYY-MM-DD')];
        // // defaultRange = [moment(startDate).format('YYYY-MM-DD'), moment(endDate).add(+1, 'days').format('YYYY-MM-DD')]
        let startDate = 0;
        let endDate = xLength - 1;
        defaultRange = [startDate, endDate];
        layout.xaxis.range = defaultRange;
        layout.xaxis.autorange = true;
      }
      if (xLength == 1) {
        // let startDate = xData[0];
        // let endDate = xData[xLength - 1];
        // defaultRange = [moment(startDate).format('YYYY-MM-DD'), moment(endDate).add(+1, 'days').format('YYYY-MM-DD')];
        let startDate = 0;
        let endDate = xLength - 1;
        defaultRange = [startDate, endDate];
        layout.xaxis.range = defaultRange;
        layout.xaxis.autorange = false;
      } else {
        layout.xaxis.range = defaultRange;
        layout.xaxis.autorange = false;
        layout.xaxis.fixedrange = false;
      }
    },

    scrollableChartSetLayoutByWeek(xLength, layout) {
      let defaultRange = [];
      // let xLength = this.lineChartData.xAxisData.length
      // over 1 year
      layout.xaxis.type = 'category';
      if (xLength > 52) {
        let startDate = xLength - 13;
        let endDate = xLength - 0.5;
        defaultRange = [startDate, endDate];
        layout.xaxis.autorange = false;
      }
      // 3 month to 1 year
      if (xLength > 12 && xLength <= 52) {
        let startDate = xLength - 10;
        let endDate = xLength - 0.5;
        defaultRange = [startDate, endDate];
        layout.xaxis.autorange = false;
        // defaultRange = [moment(startDate).format('YYYY-MM-DD'), moment(endDate).format('YYYY-MM-DD')]
      }
      // 1-3 month
      if (xLength > 4 && xLength <= 12) {
        let startDate = xLength - 5;
        let endDate = xLength - 0.5;
        defaultRange = [startDate, endDate];
        layout.xaxis.autorange = true;
        // layout.xaxis.autorange = false;
      }
      // 7 days to  1 month
      if (xLength <= 4) {
        let startDate = 0;
        let endDate = xLength - 0.5;
        defaultRange = [startDate, endDate];
        layout.xaxis.range = defaultRange;
        layout.xaxis.autorange = true;
      } else {
        layout.xaxis.fixedrange = false;
        layout.xaxis.range = defaultRange;
      }
    },

    scrollableChartSetLayoutByMonth(xLength, layout) {
      let defaultRange = [];
      // let xLength = categary.length
      // over 1 year
      layout.xaxis.type = 'category';
      if (xLength > 12) {
        let startDate = xLength - 5;
        let endDate = xLength - 0.5;
        defaultRange = [startDate, endDate];
        layout.xaxis.autorange = false;
      }
      // 5 - 12 month
      if (xLength > 5 && xLength <= 12) {
        let startDate = 3;
        let endDate = xLength - 0.5;
        defaultRange = [startDate, endDate];
        layout.xaxis.autorange = false;
      }
      // less than 5 month
      if (xLength <= 5) {
        let startDate = -0.5;
        let endDate = xLength - 0.5;
        defaultRange = [startDate, endDate];
        layout.xaxis.range = defaultRange;
        layout.xaxis.autorange = true;
        // layout.xaxis.autorange = false;
      } else {
        layout.xaxis.fixedrange = false;
        layout.xaxis.range = defaultRange;
      }
    },

    scrollableChartSetLayoutByQuarter(xLength, layout) {
      let defaultRange = [];
      // let xLength = categary.length
      // over 1 year
      layout.xaxis.type = 'category';
      if (xLength > 12) {
        let startDate = xLength - 5;
        let endDate = xLength - 0.5;
        defaultRange = [startDate, endDate];
        layout.xaxis.autorange = false;
      }
      // 5 - 12 month
      if (xLength > 5 && xLength <= 12) {
        let startDate = 3;
        let endDate = xLength - 0.5;
        defaultRange = [startDate, endDate];
        layout.xaxis.autorange = false;
      }
      // less than 5 month
      if (xLength <= 5) {
        let startDate = -0.5;
        let endDate = xLength - 0.5;
        defaultRange = [startDate, endDate];
        layout.xaxis.range = defaultRange;
        layout.xaxis.autorange = true;
        // layout.xaxis.autorange = false;
      } else {
        layout.xaxis.fixedrange = false;
        layout.xaxis.range = defaultRange;
      }
    },

    scrollableChartSetLayoutByYear(xLength, layout) {
      let defaultRange = [];
      // let xLength = this.lineChartData.xAxisData.length;
      // more thant 4 years
      layout.xaxis.type = 'category';
      if (xLength > 4) {
        let startDate = xLength - 5;
        let endDate = xLength - 0.5;
        defaultRange = [startDate, endDate];
        layout.xaxis.autorange = false;
      }
      // less than 5 years
      if (xLength <= 4) {
        let startDate = xLength - 2;
        let endDate = xLength - 0.5;
        defaultRange = [startDate, endDate];
        layout.xaxis.range = defaultRange;
        layout.xaxis.autorange = true;
      } else {
        // layout.xaxis.autorange = false
        // layout.xaxis.autorange = true;
        layout.xaxis.fixedrange = false;
        layout.xaxis.range = defaultRange;
      }
    },

    formatDecimalData(number) {
      let returnValue = 0;
      if (number >= 0.1) {
        returnValue = number.toFixed(1);
      } else {
        returnValue = '< 0.1';
      }
      return returnValue;
    },
  },
});
</script>

<style lang="scss">
.wy-analytics-contact-reasons-detail-metric-analysis-chart {
  font-family: Helvetica Neue, Helvetica, Arial, sans-serif !important;

  .signal-wrapper {
    margin-top: 20px;
    border: 1px solid #ebeef5;
    border-top-left-radius: 8px;
    border-top-right-radius: 8px;

    .signal-header {
      font-weight: 500;
      font-size: 18px;

      border-bottom: 1px solid #ebeef5;
      padding: 14px 20px;
      cursor: default;
    }

    .signal-body {
      padding: 15px 15px;

      .signal-body-dropdown {
        display: flex;
        justify-content: flex-end;
      }

      .bar-chart-wrapper {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        .js-plotly-plot {
          width: 70%;
          .plot-container.plotly {
            width: 100%;
            .rangeslider-mask-min,
            .rangeslider-mask-max {
              opacity: 0.4 !important;
            }
          }
        }
        // .svg-container {
        width: 100% !important;
        .js-plotly-plot .plotly .cursor-ew-resize {
          cursor: pointer !important;
        }
        .js-plotly-plot .plotly .main-svg {
          cursor: pointer !important;
        }
        // }
      }

      .graph-footer {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 20px;
        padding: 10px;
        padding-top: 24px; // account for panel padding (14px 20px)
        border-top: 1px solid #ebeef5;
        flex-wrap: wrap;
        cursor: default;
      }

      .footer-legend {
        display: inline-block;
        width: 10px;
        height: 10px;
        border-radius: 50%;
        margin-right: 5px;
      }
    }

    .signal-dropdown {
      width: 125px;
      padding: 4px 10px;
      padding-right: 0px;
      border-radius: 5px;
      border: 1.5px solid #d9d9d9;
      cursor: pointer;
      &:hover {
        background-color: #f4f6f8;
      }
      .signal-dropdown-title {
        display: flex;
        align-items: center;
        justify-content: space-evenly;
        color: var(--site-font-chart-color);
      }

      .arrow-up {
        background-image: url('../../assets/icons/icon_chevron_up.svg');
        background-size: 80%;
        background-repeat: no-repeat;
        height: 20px;
        width: 20px;
        background-position-y: 2px;
        background-position-x: 2px;
      }

      .arrow-down {
        background-image: url('../../assets/icons/icon_chevron_down.svg');
        background-size: 80%;
        background-repeat: no-repeat;
        height: 20px;
        width: 20px;
        background-position-y: 2px;
        background-position-x: 2px;
      }

      &.active {
        border: 1px solid #1a55ad;
        background-color: white;
      }
    }
  }

  .rangeSlider {
    display: flex;
    justify-content: flex-end;
    margin-bottom: 12px;
    .el-switch.is-checked .el-switch__core {
      border-color: #1a55ad;
      background-color: #1a55ad;
    }
    .el-switch__label.is-active {
      color: var(--site-font-tertiary-color);
    }
  }

  rect.rangeslider-slidebox {
    cursor: grab !important;
  }
}

.dragcover {
  cursor: grab !important;
}
.signal-bar-tooltip {
  position: absolute;
  // position: relative;
  color: rgb(0, 0, 0);
  padding: 20px;
  background: rgb(255, 255, 255);
  border-radius: 3px;
  z-index: 10000;
  display: flex;
  flex-direction: column;
  gap: 14px;
  pointer-events: none;
  top: -400px !important;
  // add shadow
  box-shadow: 0px 0px 10px 0px rgba(0, 0, 0, 0.2);
}
.signal-content-wrapper {
  width: 130px;
  .el-dropdown-menu {
    padding: 0px;
  }
  .el-dropdown-menu__item {
    padding: 0px;
  }
}

.signal-content-item {
  padding: 2px;
  padding-left: 10px;
  color: var(--site-font-chart-color);
  &.active {
    background-color: #e8f1ff;
    color: #1a55ad;
  }
  &:hover {
    background-color: #f4f6f8;
  }
  &.disabled {
    cursor: not-allowed;
    opacity: 0.5;
    &:hover {
      background-color: red !important;
    }
  }
}
</style>
